import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Modal, Segmented, Table, Typography, Space, Select } from 'antd';
import ExpandCollapseAllButton from '../../common/ExpandCollapseAllButton';
import NumberInput from '../../common/NumberInput';
import { InfoCircleOutlined, FolderAddOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTender } from '../../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useCanConfigureBoq } from '@/hooks/useCanConfigureBoq';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useRoadsAndSewersMarkupFactor } from '../../../hooks/useRoadsAndSewersMarkupFactor';
import { useBoqLatestVersion } from '../../../hooks/useBillOfQuantities';
import { flattenBoqSummaryLineItems } from '../../../utils/roadsAndSewersBoqSummaryComputer';
import { BcCalculationDetails, computeBcCalculation } from '../../common/BcCalculationDetails';
import type { BoqSummaryLineItem } from '../../../utils/groundworksBoqSummaryComputer';
import { COLORS, formatUnit } from '../../../utils/constants';
import {
  buildFoulSummaryData,
  type FoulSummaryRow,
  type FoulFormulaInfo,
} from '../../../utils/foulSummaryUtils';
import { PUMP_STATION_ROWS, RISING_MAIN_ROWS, PUMP_STATION_GENERAL_SECTION, applyPumpStationSectionTitles, applyPumpStationLadderType } from '../../../utils/foulBuildUpRows';
import type { PumpChamberConfigForEvaluateCode } from '../../../utils/foulBuildUpEvaluateCodesMapping';
import type { PumpChamberConfig, DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';
import FoulBuildUpBillSummary from './FoulBuildUpBillSummary';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows, type BillSummaryExtraRow } from '../../../hooks/useBillSummaryExtraItems';

const { Text, Title } = Typography;

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'section') {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Ad-hoc extra rows (useBillSummaryExtraItems.tsx) — this table's rows use
// FoulSummaryRow's own 'section'/'pipe-type'/'item' rowTypes rather than the
// generic sub-header/group convention elsewhere, so extra rows are adapted
// into that shape locally (not by editing foulSummaryUtils.ts) since these
// fields only ever appear on synthetic rows this component builds itself.
// Extra items can attach at the root ("__root__" bucket) or graft directly
// onto any real section/pipe-type row (that row's own `key` as bucket) —
// same convention groundworks/GroundworksCutFillBillSummary.tsx follows.
type ExtFoulRow = FoulSummaryRow & {
  isExtra?: boolean;
  nodeType?: 'section' | 'item';
  amountType?: 'number' | 'text';
  amountText?: string;
  // Pre-computed rate/amount for extra rows — bypasses the live evaluateCode/
  // rateMap lookup every real row uses, since a multi-code extra item has no
  // single evaluateCode to look up (buildRootExtraRows already computed the
  // right combined rate/amount, marked up the same way this table's own
  // Rate column now is).
  presetRate?: number;
  presetAmount?: number;
  // Which extraItems bucket this row lives in ("__root__", or a real row's
  // own key) — the Actions column's Edit/Delete/Add Item calls need this to
  // address the right bucket instead of assuming root.
  attachKey?: string;
};

const extraRowToFoulRow = (row: BillSummaryExtraRow): ExtFoulRow => ({
  key: row.key,
  // Real 'section' rows carry their title on `.description` (Reference column
  // reads `record.description` for rowType 'section', `.reference` for
  // 'pipe-type' — see the Reference column's own render function below).
  rowType: row.nodeType === 'section' ? 'section' : 'item',
  description: row.description,
  quantity: row.quantity,
  unit: row.unit,
  isExtra: true,
  nodeType: row.nodeType,
  amountType: row.amountType,
  amountText: row.amountText,
  presetRate: row.amountType === 'text' ? undefined : parseFloat(row.rate || '0') || undefined,
  presetAmount: row.amountType === 'text' ? undefined : parseFloat(row.amount || '0') || undefined,
  attachKey: row.attachKey,
  children: row.children?.map(extraRowToFoulRow),
});

const fmt = (n: number) =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCurrency = (n: number) =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface FoulSewersBillSummaryProps {
  showRateAmount?: boolean;
}

const FoulSewersBillSummary: React.FC<FoulSewersBillSummaryProps> = ({ showRateAmount = true }) => {
  const showDevMode = useDevMode();
  // Ad-hoc extra rows only make sense on the review page's own editable view
  // (showRateAmount=true) — gated the same as every groundworks Bill Summary's
  // own Configure BOQ switch, further narrowed to this prop.
  const dynamicSummaryEnabled = useCanConfigureBoq() && showRateAmount;
  const { state, updateField } = useTender();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [activeFormula, setActiveFormula] = useState<FoulFormulaInfo | null>(null);
  const [activeSection, setActiveSection] = useState<'summary' | 'pump-station' | 'rising-main'>('summary');
  // SummaryKey modal — "BCCalculation" independent backend-dbRate-only recomputation,
  // same as groundworks Bill Summaries (BcCalculationDetails.tsx / computeBcCalculation).
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);

  // Build-up tabs' Expand/Collapse All state is lifted up here so its button can
  // sit on the same header line as the section Segmented control, next to the
  // Bill Summary tab's own button, instead of floating inside each table.
  const pumpStationGroupKeys = useMemo(
    () => [PUMP_STATION_GENERAL_SECTION.key, ...PUMP_STATION_ROWS.filter((r) => r.isHeader).map((r) => r.id)],
    [],
  );
  const risingMainGroupKeys = useMemo(
    () => RISING_MAIN_ROWS.filter((r) => r.isHeader).map((r) => r.id),
    [],
  );
  const [pumpStationExpandedKeys, setPumpStationExpandedKeys] = useState<React.Key[]>([]);
  const [risingMainExpandedKeys, setRisingMainExpandedKeys] = useState<React.Key[]>([]);

  const { data: itemLibraryData } = useItemLibrary(state.region || '');
  // Same OH&P/Preliminaries markup every R&S Bill Summary applies to its own
  // Rate column now — baked into rateMap below so every downstream lookup
  // (Rate/Amount columns, sumRows/rowTotals, extra items, and the
  // FoulBuildUpBillSummary child this rateMap is passed into for Pump
  // Station/Rising Main) picks it up for free.
  const markupFactor = useRoadsAndSewersMarkupFactor();

  // Ad-hoc user-added root-level Section/Item rows — see ExtFoulRow above.
  const foulSewersExtraItems = state.foulSewersExtraItems;
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);
  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: foulSewersExtraItems,
    onChange: (updated) => updateField('foulSewersExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setNewlyAddedRowId(nodeId);
    },
  });

  // Pump Station / Rising Main each get their own extra-items bucket + CRUD
  // instance, owned here (not inside FoulBuildUpBillSummary.tsx) so this
  // page's own header can render each section's "Add Section" button right
  // next to that section's Expand/Collapse All button — same convention
  // PrelimsBillSummary.tsx uses for its single root table.
  const [pumpStationNewlyAddedRowId, setPumpStationNewlyAddedRowId] = useState<string | null>(null);
  const pumpStationExtraItemsApi = useBillSummaryExtraItems({
    extraItems: state.pumpStationExtraItems,
    onChange: (updated) => updateField('pumpStationExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setPumpStationExpandedKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setPumpStationNewlyAddedRowId(nodeId);
      setTimeout(() => setPumpStationNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });
  const [risingMainNewlyAddedRowId, setRisingMainNewlyAddedRowId] = useState<string | null>(null);
  const risingMainExtraItemsApi = useBillSummaryExtraItems({
    extraItems: state.risingMainExtraItems,
    onChange: (updated) => updateField('risingMainExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setRisingMainExpandedKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setRisingMainNewlyAddedRowId(nodeId);
      setTimeout(() => setRisingMainNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });

  // Build a code→{rate} lookup directly from item library, for the SummaryKey modal's
  // "BCCalculation" line (same convention as groundworks' Bill Summaries).
  const codeRateLookup = useMemo(() => {
    const map = new Map<string, { rate: number }>();
    (itemLibraryData ?? []).forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, { rate: item.nettRate || item.baseRate || 0 });
    });
    return map;
  }, [itemLibraryData]);

  // Rate is rounded to 2dp here — the SAME value shown in the Rate column —
  // so Amount always equals the displayed Rate × displayed Qty exactly (the
  // raw item-library rate can carry more than 2dp, e.g. 2107.678, which would
  // otherwise silently drift Amount away from what the user can verify by
  // hand). Same convention every groundworks Bill Summary's Amount column uses.
  const rateMap = useMemo(() => {
    const map: Record<string, number> = {};
    (itemLibraryData ?? []).forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = parseFloat(((item.nettRate || 0) * markupFactor).toFixed(2));
    });
    return map;
  }, [itemLibraryData, markupFactor]);

  // Plain nett rate, no markup — passed to FoulBuildUpBillSummary for its own
  // dev-mode "BC" modal only (see that component's own comment).
  const nettRateMap = useMemo(() => {
    const map: Record<string, number> = {};
    (itemLibraryData ?? []).forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = parseFloat((item.nettRate || 0).toFixed(2));
    });
    return map;
  }, [itemLibraryData]);

  const foul = state.foulSewers;

  // Same guard FoulSewersTakeoff.tsx uses — an empty/never-configured pump chamber
  // config has no `coverLevel`, and the Wet Well/mesh rows' evaluate codes need a
  // real one to compute (see resolveBuildUpEvaluateCode).
  const pumpChamberConfig: PumpChamberConfigForEvaluateCode | undefined =
    (state.pumpStation?.config as PumpChamberConfig)?.coverLevel !== undefined
      ? (state.pumpStation?.config as PumpChamberConfig)
      : undefined;

  const tableData: FoulSummaryRow[] = useMemo(() => {
    const realRows = foul
      ? buildFoulSummaryData(
          foul.pipeRuns.table || [],
          foul.manholes.table || [],
          foul.lateralConnections.table || [],
          foul.pipeRuns.config.eoQtys,
          foul.manholes.config.manholeScheduleConfig,
        )
      : [];
    // Graft ad-hoc extra rows onto this otherwise-computed tree — nested
    // additions merge onto the matching real section/pipe-type row (by
    // `key`), root additions become new top-level rows. Same convention
    // groundworks/GroundworksCutFillBillSummary.tsx follows.
    const extraBuckets = foulSewersExtraItems ?? {};
    const injectExtraRows = (rowsIn: FoulSummaryRow[]): FoulSummaryRow[] =>
      rowsIn.map((row) => {
        const bucket = extraBuckets[row.key];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        const existingChildren = row.children ?? [];
        const extraRows = buildNestedExtraRows(bucket, row.key, row.key, existingChildren.length, markupFactor)
          .map(extraRowToFoulRow);
        return { ...row, children: [...(ownChildren ?? []), ...extraRows] };
      });
    const injectedRealRows = injectExtraRows(realRows);
    const extraRootRows = buildRootExtraRows(extraBuckets['__root__'] ?? [], '__root__', 1, markupFactor)
      .map(extraRowToFoulRow);
    return [...injectedRealRows, ...extraRootRows];
  }, [foul, foulSewersExtraItems, markupFactor]);

  const hasData = (foul?.pipeRuns?.table?.length ?? 0) > 0
    || (foul?.manholes?.table?.length ?? 0) > 0
    || (foul?.lateralConnections?.table?.length ?? 0) > 0
    || Object.values(foulSewersExtraItems ?? {}).some((bucket) => bucket.length > 0);

  const sumRows = (rows: (FoulSummaryRow & Partial<ExtFoulRow>)[]): number =>
    rows.reduce((acc, row) => {
      if (row.children) return acc + sumRows(row.children);
      if (row.rowType !== 'item') return acc;
      if (row.isExtra) return acc + (row.amountType === 'text' ? 0 : (row.presetAmount ?? 0));
      const qty = parseFloat(row.quantity ?? '');
      const rate = row.evaluateCode ? (rateMap[row.evaluateCode] ?? 0) : 0;
      return isNaN(qty) ? acc : acc + qty * rate;
    }, 0);

  const totalAmount = useMemo(() => sumRows(tableData), [tableData, rateMap]);

  // Dev-mode only: pulls the persisted `roadAndSewers.summary.foulSewers`
  // tree from the same boq-versions API the rest of this review page uses,
  // so the SummaryKey column reflects what was actually saved rather than
  // the live recomputation above.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );
  const persistedFoulSewers = useMemo(
    () => flattenBoqSummaryLineItems(
      (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: { foulSewers?: import('../../../utils/groundworksBoqSummaryComputer').BoqSummaryLineItem[] } } }> } })
        ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary?.foulSewers,
    ),
    [latestBoqVersionData],
  );
  const persistedPumpStation = useMemo(
    () => flattenBoqSummaryLineItems(
      (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: { pumpStation?: import('../../../utils/groundworksBoqSummaryComputer').BoqSummaryLineItem[] } } }> } })
        ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary?.pumpStation,
    ),
    [latestBoqVersionData],
  );
  const persistedRisingMain = useMemo(
    () => flattenBoqSummaryLineItems(
      (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: { risingMain?: import('../../../utils/groundworksBoqSummaryComputer').BoqSummaryLineItem[] } } }> } })
        ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary?.risingMain,
    ),
    [latestBoqVersionData],
  );

  // Pre-compute subtotals for every group row (section / pipe-type)
  const rowTotals = useMemo(() => {
    const map: Record<string, number> = {};
    const visit = (rows: FoulSummaryRow[]) => {
      rows.forEach((row) => {
        if (row.children) {
          map[row.key as string] = sumRows(row.children);
          visit(row.children);
        }
      });
    };
    visit(tableData);
    return map;
  }, [tableData, rateMap]);

  // Recurses through every level (section → pipe-type → nested groups →
  // sub-groups, e.g. Junctions' EO Conc Bed/Conc Protection sub-lists go 4-5
  // levels deep) so "Expand All" actually opens every collapsible row, not
  // just sections and their direct children — the previous 2-level walk
  // (ALL_SECTION_KEYS + one level of section.children) missed any row whose
  // own children were themselves grouped further (e.g. 'mh-credits' under
  // 'mh-adjustment', the Stub Pipe/Junction sub-groups' own item groups).
  const allKeys = useMemo(() => {
    const keys: React.Key[] = [];
    const visit = (rows: FoulSummaryRow[]) => {
      rows.forEach((row) => {
        if (row.children && row.children.length > 0) {
          keys.push(row.key);
          visit(row.children);
        }
      });
    };
    visit(tableData);
    return keys;
  }, [tableData]);

  const columns = [
    // ── Col 1: Reference — every column renders independently for every row
    // (no colSpan merge trick), same convention groundworks/GroundworksCutFillBillSummary.tsx
    // uses: a group row shows its title only in Reference/Description, blank
    // in every other column, and Amount shows the collapsed subtotal. colSpan
    // requires its value to exactly match the live column count on every
    // render (Rate/Amount/SummaryKey/Actions all toggle on/off independently
    // here), and any drift there visibly misaligns every column after it —
    // not worth the fragility for what's purely a visual "span the row" effect.
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 180,
      render: (_v: string, record: FoulSummaryRow) => {
        if (record.rowType === 'section') {
          return <Text strong className="text-indigo-600" style={{ textTransform: 'uppercase' }}>{record.description}</Text>;
        }
        if (record.rowType === 'pipe-type') {
          return <Text strong className="text-gray-700" style={{ textTransform: 'uppercase' }}>{record.reference}</Text>;
        }
        return record.reference ? <Text>{record.reference}</Text> : null;
      },
    },
    // ── Col 2: Description (evaluate code shown as sub-line) ─────────────────
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 320,
      render: (v: string, record: FoulSummaryRow) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return '';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span>
              <Text>{v}</Text>
              {showDevMode && record.formulaInfo && (
                <InfoCircleOutlined
                  style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); setActiveFormula(record.formulaInfo ?? null); }}
                />
              )}
            </span>
            {showDevMode && record.evaluateCode && (
              <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>{record.evaluateCode}</div>
            )}
          </div>
        );
      },
    },
    // ── Col 3: Qty ────────────────────────────────────────────────────────────
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
      render: (v: string, record: FoulSummaryRow & Partial<ExtFoulRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <NumberInput
              value={parseFloat(v) || 0}
              onChange={(qty) => extraItemsApi.updateNode(record.attachKey ?? '__root__', record.key as string, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={record.key === newlyAddedRowId}
            />
          );
        }
        const n = parseFloat(v ?? '');
        if (isNaN(n)) return record.key === 'flat-1200-chamber' ? null : <Text>0</Text>;
        return <Text style={{ color: n < 0 ? '#cf1322' : undefined }}>{Math.round(n).toLocaleString('en-GB')}</Text>;
      },
    },
    // ── Col 4: Unit ───────────────────────────────────────────────────────────
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (v: string, record: FoulSummaryRow & Partial<ExtFoulRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <Select
              value={v || undefined}
              onChange={(unit) => extraItemsApi.updateNode(record.attachKey ?? '__root__', record.key as string, { unit })}
              options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
              placeholder="Unit"
              size="small"
              showSearch
              allowClear
              style={{ width: '100%' }}
            />
          );
        }
        return formatUnit(v) || '';
      },
    },
    // ── Col 5: Rate ───────────────────────────────────────────────────────────
    ...(showRateAmount ? [{
      title: 'Rate (£)',
      key: 'rate',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, record: FoulSummaryRow & Partial<ExtFoulRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra) {
          return record.presetRate != null ? <Text>{fmt(record.presetRate)}</Text> : <Text type="secondary">—</Text>;
        }
        if (!record.evaluateCode) return <Text type="secondary">—</Text>;
        const rate = rateMap[record.evaluateCode];
        return rate != null ? <Text>{fmt(rate)}</Text> : <Text type="secondary">—</Text>;
      },
    }] : []),
    // ── Col 6: Amount ─────────────────────────────────────────────────────────
    ...(showRateAmount ? [{
      title: 'Amount (£)',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: FoulSummaryRow & Partial<ExtFoulRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type') {
          const isCollapsed = !expandedRowKeys.includes(record.key);
          if (isCollapsed) {
            const subtotal = rowTotals[record.key as string] ?? 0;
            return subtotal > 0
              ? <Text strong>{fmtCurrency(subtotal)}</Text>
              : null;
          }
          return '';
        }
        if (record.amountType === 'text') return <Text>{record.amountText}</Text>;
        if (record.isExtra) {
          return record.presetAmount != null ? <Text>{fmt(record.presetAmount)}</Text> : <Text type="secondary">—</Text>;
        }
        if (!record.evaluateCode) return <Text type="secondary">—</Text>;
        const qty = parseFloat(record.quantity ?? '');
        const rate = rateMap[record.evaluateCode];
        if (isNaN(qty) || rate == null) return <Text type="secondary">—</Text>;
        return <Text>{fmt(qty * rate)}</Text>;
      },
    }] : []),
    // ── Dev-mode only: persisted-vs-live comparison column ───────────────────
    ...(showDevMode && showRateAmount ? [{
      title: 'SummaryKey',
      key: 'summaryKey',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: FoulSummaryRow & Partial<ExtFoulRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const persisted = persistedFoulSewers.get(record.key as string);
        if (!persisted) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const qty = parseFloat(record.quantity ?? '');
        const rate = record.evaluateCode ? rateMap[record.evaluateCode] : undefined;
        const liveAmount = !isNaN(qty) && rate != null ? qty * rate : 0;
        const mismatch = Math.abs(liveAmount - (persisted.amount || 0)) > 0.01;
        const bcAmount = computeBcCalculation(persisted, (code) => codeRateLookup.get(code)?.rate ?? 0).amount;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                {fmtCurrency(persisted.amount || 0)}
              </Text>
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: '#3b82f6' }}
                onClick={() => setSummaryKeyModalRecord(persisted)}
              />
            </div>
            <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
              BC: {fmtCurrency(bcAmount)}
            </Text>
          </div>
        );
      },
    }] : []),
    // ── Add Section/Add Item on any real or extra section/pipe-type row,
    // Edit/Delete on extra rows — same convention
    // groundworks/GroundworksCutFillBillSummary.tsx follows: a real header
    // row is immutable itself but can still host grafted extra rows. ────────
    ...(dynamicSummaryEnabled && !state.isViewMode ? [{
      title: 'Actions',
      key: 'extraActions',
      width: 150,
      align: 'center' as const,
      render: (_: unknown, record: FoulSummaryRow & Partial<ExtFoulRow>) => {
        if (record.isExtra) {
          const bucketKey = record.attachKey ?? '__root__';
          const node = findExtraNode(foulSewersExtraItems?.[bucketKey] ?? [], record.key as string);
          if (!node) return '';
          if (record.nodeType === 'section') {
            return (
              <Space size={4}>
                <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(bucketKey, record.key as string)} />
                <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(bucketKey, node as DynamicBillSummarySectionNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, record.key as string)} />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(bucketKey, node as DynamicBillSummaryItemNode)} />
              <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, record.key as string)} />
            </Space>
          );
        }
        // Real section/pipe-type row — immutable itself, but can host grafted
        // extra rows (its own `key` becomes that bucket's attachKey).
        if (record.rowType === 'section' || record.rowType === 'pipe-type') {
          return (
            <Space size={4}>
              <Button size="small" icon={<FolderAddOutlined />} title="Add Section" onClick={() => extraItemsApi.openAddSection(record.key as string, null)} />
              <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key as string, null)} />
            </Space>
          );
        }
        return '';
      },
    }] : []),
  ];

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ margin: '0 0 12px', fontWeight: 600, fontSize: '16px', color: '#111827' }}>
          Foul Sewers Bill Summary
        </Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <Segmented
            value={activeSection}
            onChange={(value) => setActiveSection(value as typeof activeSection)}
            options={[
              { label: 'Bill Summary', value: 'summary' },
              { label: 'Pump Station', value: 'pump-station' },
              { label: 'Rising Main', value: 'rising-main' },
            ]}
          />
          {activeSection === 'summary' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={expandedRowKeys.length > 0}
                onToggle={() => setExpandedRowKeys(expandedRowKeys.length > 0 ? [] : allKeys)}
              />
            </Space>
          )}
          {activeSection === 'pump-station' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => pumpStationExtraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={pumpStationExpandedKeys.length > 0}
                onToggle={() => setPumpStationExpandedKeys(pumpStationExpandedKeys.length > 0 ? [] : pumpStationGroupKeys)}
              />
            </Space>
          )}
          {activeSection === 'rising-main' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => risingMainExtraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={risingMainExpandedKeys.length > 0}
                onToggle={() => setRisingMainExpandedKeys(risingMainExpandedKeys.length > 0 ? [] : risingMainGroupKeys)}
              />
            </Space>
          )}
        </div>
      </div>

      <style>{`
        .foul-bill-table .ant-table { border-radius: 0 !important; }
        .foul-bill-table .ant-table-container { border-radius: 0 !important; }
        .foul-bill-table .ant-table-thead > tr > th { background-color: ${COLORS.tableHeaderBg} !important; }
        .foul-bill-table .ant-table-tbody > tr:hover > td { background-color: #fafafa !important; }
        .foul-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td { background-color: #e0e7ff !important; }
        .foul-bill-table .ant-table-tbody > tr.bg-gray-50:hover > td { background-color: #f3f4f6 !important; }
      `}</style>

      {activeSection === 'summary' && (
        hasData ? (
          <Table<FoulSummaryRow>
            className="foul-bill-table"
            dataSource={tableData}
            columns={columns}
            rowKey="key"
            bordered
            size="middle"
            pagination={false}
            expandable={{
              childrenColumnName: 'children',
              expandedRowKeys,
              onExpand: (expanded, record) => {
                setExpandedRowKeys(
                  expanded
                    ? [...expandedRowKeys, record.key]
                    : expandedRowKeys.filter((k) => k !== record.key),
                );
              },
              expandRowByClick: true,
            }}
            rowClassName={(record: FoulSummaryRow & Partial<ExtFoulRow>) => {
              const classes: string[] = [];
              if (record.rowType === 'section') classes.push('bg-indigo-50', 'font-semibold');
              else if (record.rowType === 'pipe-type') classes.push('bg-gray-50', 'font-semibold');
              if (record.key === newlyAddedRowId) classes.push('foul-summary-row-added');
              return classes.join(' ');
            }}
            summary={() => (
              <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={showRateAmount ? 5 : 4}>
                  <Text strong style={{ fontWeight: 700 }}>TOTAL FOUL SEWERS</Text>
                </Table.Summary.Cell>
                {showRateAmount && (
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>{fmtCurrency(totalAmount)}</Text>
                  </Table.Summary.Cell>
                )}
                {showDevMode && showRateAmount && <Table.Summary.Cell index={2} />}
                {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={showDevMode && showRateAmount ? 3 : 2} />}
              </Table.Summary.Row>
            )}
          />
        ) : (
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <Text type="secondary">No foul sewer data entered. Enter data on the Foul Sewers Take-off page.</Text>
          </div>
        )
      )}
      <style>{`
        @keyframes foul-summary-row-added-fade { 0% { background-color: #fef3c7; } 100% { background-color: transparent; } }
        .foul-summary-row-added > td { animation: foul-summary-row-added-fade 1.8s ease-out; }
      `}</style>

      {activeSection === 'pump-station' && (
        <FoulBuildUpBillSummary
          title="Pump Station"
          rows={applyPumpStationSectionTitles(
            applyPumpStationLadderType(PUMP_STATION_ROWS, state.foulSewers?.manholes?.config?.manholeScheduleConfig?.ladders, state.pumpStation?.config as PumpChamberConfig | undefined),
            (state.pumpStation?.config as PumpChamberConfig | undefined)?.sectionTitles,
          )}
          buildUp={state.pumpStation?.table}
          rateMap={rateMap}
          nettRateMap={nettRateMap}
          markupFactor={markupFactor}
          showRateAmount={showRateAmount}
          pumpChamberConfig={pumpChamberConfig}
          ungroupedSection={PUMP_STATION_GENERAL_SECTION}
          expandedRowKeys={pumpStationExpandedKeys}
          onExpandedRowKeysChange={setPumpStationExpandedKeys}
          persistedItems={persistedPumpStation}
          extraItems={state.pumpStationExtraItems}
          extraItemsApi={pumpStationExtraItemsApi}
          dynamicSummaryEnabled={dynamicSummaryEnabled}
          newlyAddedRowId={pumpStationNewlyAddedRowId}
        />
      )}

      {activeSection === 'rising-main' && (
        <FoulBuildUpBillSummary
          title="Rising Main"
          rows={RISING_MAIN_ROWS}
          buildUp={state.risingMain?.table}
          rateMap={rateMap}
          nettRateMap={nettRateMap}
          markupFactor={markupFactor}
          showRateAmount={showRateAmount}
          expandedRowKeys={risingMainExpandedKeys}
          onExpandedRowKeysChange={setRisingMainExpandedKeys}
          persistedItems={persistedRisingMain}
          extraItems={state.risingMainExtraItems}
          extraItemsApi={risingMainExtraItemsApi}
          dynamicSummaryEnabled={dynamicSummaryEnabled}
          newlyAddedRowId={risingMainNewlyAddedRowId}
        />
      )}

      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => codeRateLookup.get(code)?.rate ?? 0}
          />
        )}
      </Modal>

      {extraItemsApi.modals}

      <Drawer
        title={activeFormula?.label ?? 'Formula Detail'}
        open={!!activeFormula}
        onClose={() => setActiveFormula(null)}
        width={460}
        styles={{ body: { padding: '16px' } }}
      >
        {activeFormula && (
          <div>
            <Typography.Paragraph>
              <Text strong>Formula: </Text>
              <Text code>{activeFormula.formula}</Text>
            </Typography.Paragraph>
            {activeFormula.rows.length > 0 && (
              <Table
                size="small"
                bordered
                pagination={false}
                dataSource={activeFormula.rows.map((r, i) => ({ ...r, key: i }))}
                columns={[
                  { title: 'Source', dataIndex: 'ref', key: 'ref' },
                  {
                    title: 'Value', dataIndex: 'value', key: 'value', align: 'right' as const,
                    render: (v: number) => fmt(v),
                  },
                  { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'right' as const, render: (v: string) => formatUnit(v) },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>{fmt(activeFormula.total)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                  </Table.Summary.Row>
                )}
              />
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default FoulSewersBillSummary;
